import type { VoucherCodeGroup, VoucherListParams, VoucherSort, VoucherStatus } from '@/marketing/models/voucher';

export type VoucherStatusFilter = VoucherStatus | 'all';

export type VoucherCodeGroupFilter = VoucherCodeGroup | 'all';

/** The campaign part of the filter state, which one select edits. */
export interface VoucherCampaignFilter {
  codeGroup: VoucherCodeGroupFilter;
  /** One affiliate campaign; empty for none. */
  campaignId: string;
}

export interface VoucherFilterState {
  search: string;
  status: VoucherStatusFilter;
  codeGroup: VoucherCodeGroupFilter;
  campaignId: string;
  /** Date-input values (YYYY-MM-DD); the listing route wants ISO8601 instants. */
  fromDate: string;
  toDate: string;
  sort: VoucherSort;
  pageIndex: number;
  pageSize: number;
}

export const DEFAULT_VOUCHER_PAGE_SIZE = 50;

export const DEFAULT_VOUCHER_FILTERS: VoucherFilterState = {
  search: '',
  status: 'all',
  codeGroup: 'all',
  campaignId: '',
  fromDate: '',
  toDate: '',
  sort: 'createdAt',
  pageIndex: 0,
  pageSize: DEFAULT_VOUCHER_PAGE_SIZE,
};

const DATE_INPUT_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

function toDayStartInstant(date: string): string | null {
  return DATE_INPUT_PATTERN.test(date) ? `${date}T00:00:00.000Z` : null;
}

function toDayEndInstant(date: string): string | null {
  return DATE_INPUT_PATTERN.test(date) ? `${date}T23:59:59.999Z` : null;
}

/** Only the filters the admin actually set are sent, so the backend applies its own defaults for the rest. */
export function toVoucherListParams(filters: VoucherFilterState): VoucherListParams {
  const search = filters.search.trim();
  const fromInstant = toDayStartInstant(filters.fromDate);
  const toInstant = toDayEndInstant(filters.toDate);

  return {
    ...(search.length > 0 ? { search } : {}),
    ...(filters.status !== 'all' ? { status: filters.status } : {}),
    ...(filters.campaignId ? { campaignId: filters.campaignId } : filters.codeGroup !== 'all' ? { codeGroup: filters.codeGroup } : {}),
    ...(fromInstant ? { from: fromInstant } : {}),
    ...(toInstant ? { to: toInstant } : {}),
    sort: filters.sort,
    limit: filters.pageSize,
    offset: filters.pageIndex * filters.pageSize,
  };
}

const CODE_GROUP_FILTERS: VoucherCodeGroupFilter[] = ['all', 'personal', 'campaign'];

/** One select offers every code, a code group or a single campaign; a value outside the groups is a campaign id. */
export function toVoucherCampaignFilter(selectValue: string): VoucherCampaignFilter {
  if ((CODE_GROUP_FILTERS as string[]).includes(selectValue)) {
    return { codeGroup: selectValue as VoucherCodeGroupFilter, campaignId: '' };
  }
  return { codeGroup: 'campaign', campaignId: selectValue };
}

export function toVoucherCampaignFilterValue(filters: VoucherCampaignFilter): string {
  return filters.campaignId || filters.codeGroup;
}
